import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  NOTIFICATION_CENTER_DEADLINES_TAB_ID,
  NOTIFICATION_CENTER_PAGE_SIZE,
  NOTIFICATION_CENTER_REGISTRATIONS_TAB_ID,
  NOTIFICATION_CENTER_REPORTING_TAB_ID,
  NOTIFICATION_CENTER_TAB_KEYS,
  isNotificationCenterPaginatedTable,
} from "./notificationCenterConstants";
import {
  getNotificationCenterCounters,
  getNotificationCenterTable,
} from "./notificationCenterApi";
import {
  getNotificationCenterShowAllQueryParams,
  resolveNotificationCenterShowAllForApi,
} from "../../../containers/NotificationCenter/utils/notificationCenterAccess";
import {
  NOTIFICATION_CENTER_DEADLINES_SCOPE,
  NOTIFICATION_CENTER_DECLARATION_TYPE,
  NOTIFICATION_CENTER_VISIBILITY,
} from "../../../containers/NotificationCenter/utils/notificationCenterFilterConstants";
import {
  applyStoredNotificationCenterFiltersToState,
  loadNotificationCenterFiltersFromStorage,
  saveNotificationCenterFiltersToStorage,
} from "../../../containers/NotificationCenter/utils/notificationCenterFiltersStorage";
import {
  collectNotificationCenterRegistrationsFilterOptions,
  createEmptyRegistrationsFilterOptions,
  getNotificationCenterRegistrationsFilterSignature,
  getNotificationCenterRegistrationsQueryParams,
  normalizeRegistrationsCompanyFilter,
  normalizeRegistrationsDateFilter,
  normalizeRegistrationsJurisdictionFilter,
  normalizeRegistrationsMultiSelectFilter,
  normalizeRegistrationsOrderDateFilter,
  normalizeRegistrationsTypeFilter,
} from "../../../containers/NotificationCenter/utils/notificationCenterRegistrationsFilters";
import { getNotificationCenterRegistrationTrackerId } from "../../../containers/NotificationCenter/utils/notificationCenterRegistrationHighlight";
import { updateRegistrationTrackerHighlighted } from "../registration-tracker/registrationTrackerSlice";
import { login } from "../auth/authSlice";
function normalizeList(value) {
  if (Array.isArray(value)) return value;
  if (value?.items && Array.isArray(value.items)) return value.items;
  if (value?.data && Array.isArray(value.data)) return value.data;
  if (value?.results && Array.isArray(value.results)) return value.results;
  return [];
}

function createEmptyByTab() {
  return {};
}

function createEmptyByTableCounts() {
  return {};
}

function createEmptyTabTableState(limit = NOTIFICATION_CENTER_PAGE_SIZE) {
  return {
    loading: false,
    error: null,
    default: {
      items: [],
      total: 0,
      offset: 0,
      limit,
    },
  };
}

/**
 * API shape: { total: number, by_table: { reporting?: number, ... } }
 */
export function normalizeNotificationCenterCounters(raw) {
  if (!raw || typeof raw !== "object") {
    return { total: 0, byTable: createEmptyByTableCounts(), allowedTableIds: [] };
  }

  const source =
    raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : raw;

  const incoming = source.by_table ?? source.byTable ?? {};
  const knownTableIds = Object.keys(incoming).filter((tabId) =>
    NOTIFICATION_CENTER_TAB_KEYS.includes(tabId)
  );
  const byTable = createEmptyByTableCounts();
  for (const tabId of knownTableIds) {
    byTable[tabId] = Number(incoming[tabId]) || 0;
  }
  const allowedTableIds = knownTableIds.filter((tabId) => byTable[tabId] > 0);

  return {
    total: Number(source.total) || 0,
    byTable,
    allowedTableIds,
  };
}

/**
 * GET /notify/notification_center/:table_id
 * { table_id, total, offset, limit, items: Row[] }
 */
export function normalizeNotificationCenterTableResponse(raw, tableId, fallbackOffset, fallbackLimit) {
  const source =
    raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : raw;

  const parsedOffset = Number(source?.offset);
  const parsedLimit = Number(source?.limit);

  return {
    tableId: source?.table_id ?? tableId,
    items: normalizeList(source?.items),
    total: Number(source?.total) || 0,
    offset: Number.isFinite(parsedOffset) ? parsedOffset : fallbackOffset,
    limit: Number.isFinite(parsedLimit) ? parsedLimit : fallbackLimit,
  };
}

function isSilentFetch(arg) {
  return Boolean(arg && typeof arg === "object" && arg.silent);
}

function resolveDeclarationTypeFilter(declarationType, stateFilter) {
  const allowed = Object.values(NOTIFICATION_CENTER_DECLARATION_TYPE);
  if (allowed.includes(declarationType)) {
    return declarationType;
  }
  if (allowed.includes(stateFilter)) {
    return stateFilter;
  }
  return NOTIFICATION_CENTER_DECLARATION_TYPE.ALL;
}

/**
 * @param {string} declarationType
 * @returns {Record<string, string>}
 */
export function getNotificationCenterDeclarationTypeQueryParams(declarationType) {
  const resolved = resolveDeclarationTypeFilter(
    declarationType,
    NOTIFICATION_CENTER_DECLARATION_TYPE.ALL
  );
  if (resolved === NOTIFICATION_CENTER_DECLARATION_TYPE.ALL) {
    return {};
  }
  return { declaration_type: resolved };
}

function tableRequestKey(
  tableId,
  offset,
  limit,
  showAll = false,
  declarationType = NOTIFICATION_CENTER_DECLARATION_TYPE.ALL,
  registrationsFilterSignature = "none"
) {
  const filter =
    tableId === NOTIFICATION_CENTER_REPORTING_TAB_ID
      ? resolveDeclarationTypeFilter(
          declarationType,
          NOTIFICATION_CENTER_DECLARATION_TYPE.ALL
        )
      : NOTIFICATION_CENTER_DECLARATION_TYPE.ALL;
  const registrations =
    tableId === NOTIFICATION_CENTER_REGISTRATIONS_TAB_ID
      ? registrationsFilterSignature
      : "none";
  return `${tableId}:${offset}:${limit}:${showAll ? 1 : 0}:${filter}:${registrations}`;
}

/** Registrations filters live in the slice; requests always read the current state. */
function resolveRegistrationsFilters(tableId, state) {
  if (tableId !== NOTIFICATION_CENTER_REGISTRATIONS_TAB_ID) {
    return null;
  }
  return {
    registrationsCompanyFilter: state?.registrationsCompanyFilter ?? null,
    registrationsOrderDateFilter: state?.registrationsOrderDateFilter ?? null,
    registrationsJurisdictionFilter:
      state?.registrationsJurisdictionFilter ?? [],
    registrationsTypeOfRegistrationFilter:
      state?.registrationsTypeOfRegistrationFilter ?? null,
    registrationsSubcategoryFilter:
      state?.registrationsSubcategoryFilter ?? [],
    registrationsStatusFilter: state?.registrationsStatusFilter ?? [],
    registrationsDateLastStatusChangedFilter:
      state?.registrationsDateLastStatusChangedFilter ?? null,
    registrationsRegistratorFilter:
      state?.registrationsRegistratorFilter ?? [],
    registrationsEstimatedRegistrationDateFilter:
      state?.registrationsEstimatedRegistrationDateFilter ?? null,
  };
}

/** Prevents duplicate in-flight GETs for the same table page (e.g. Strict Mode / double effect). */
const inFlightTableRequests = new Set();
const inFlightDeadlinesRequests = new Set();

function resolveDeadlinesFilter(deadlineFilter, stateFilter) {
  const allowed = Object.values(NOTIFICATION_CENTER_DEADLINES_SCOPE);
  if (allowed.includes(deadlineFilter)) {
    return deadlineFilter;
  }
  if (allowed.includes(stateFilter)) {
    return stateFilter;
  }
  return NOTIFICATION_CENTER_DEADLINES_SCOPE.NEXT_3_DAYS;
}

function deadlinesRequestKey(
  showAll = false,
  deadlineFilter,
  offset = 0,
  limit = NOTIFICATION_CENTER_PAGE_SIZE
) {
  const filter = resolveDeadlinesFilter(
    deadlineFilter,
    NOTIFICATION_CENTER_DEADLINES_SCOPE.NEXT_3_DAYS
  );
  return `${NOTIFICATION_CENTER_DEADLINES_TAB_ID}:${showAll ? 1 : 0}:${filter}:${offset}:${limit}`;
}

/**
 * GET /notify/notification_center/deadlines?deadline_filter=&offset=&limit=
 * Standard table shape `{ items, total, offset, limit }`; legacy `{ deadlines }` fallback.
 */
export function normalizeNotificationCenterDeadlinesResponse(
  raw,
  fallbackOffset = 0,
  fallbackLimit = NOTIFICATION_CENTER_PAGE_SIZE
) {
  const source =
    raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : raw;

  if (
    Array.isArray(source?.items) ||
    source?.total != null ||
    source?.offset != null ||
    source?.limit != null
  ) {
    return normalizeNotificationCenterTableResponse(
      raw,
      NOTIFICATION_CENTER_DEADLINES_TAB_ID,
      fallbackOffset,
      fallbackLimit
    );
  }

  const items = normalizeList(source?.deadlines);
  return {
    tableId: NOTIFICATION_CENTER_DEADLINES_TAB_ID,
    items,
    total: items.length,
    offset: Number(fallbackOffset) || 0,
    limit: Number(fallbackLimit) || NOTIFICATION_CENTER_PAGE_SIZE,
  };
}

export const fetchNotificationCenterDeadlines = createAsyncThunk(
  "notificationCenter/fetchDeadlines",
  async (
    {
      silent = false,
      offset = 0,
      limit = NOTIFICATION_CENTER_PAGE_SIZE,
      showAll = false,
      deadlineFilter,
    } = {},
    { rejectWithValue, getState }
  ) => {
    const resolvedDeadlineFilter = resolveDeadlinesFilter(
      deadlineFilter,
      getState().notificationCenter?.deadlinesScopeFilter
    );
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(1, Number(limit) || NOTIFICATION_CENTER_PAGE_SIZE);

    try {
      const response = await getNotificationCenterTable(
        NOTIFICATION_CENTER_DEADLINES_TAB_ID,
        {
          deadline_filter: resolvedDeadlineFilter,
          offset: safeOffset,
          limit: safeLimit,
          ...getNotificationCenterShowAllQueryParams({
            showAll,
            tableId: NOTIFICATION_CENTER_DEADLINES_TAB_ID,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }

      const data = await response.json();
      return {
        ...normalizeNotificationCenterDeadlinesResponse(data, safeOffset, safeLimit),
        silent,
        deadlineFilter: resolvedDeadlineFilter,
        showAll,
      };
    } catch (e) {
      return rejectWithValue({
        tableId: NOTIFICATION_CENTER_DEADLINES_TAB_ID,
        message: e?.message || "Error",
        deadlineFilter: resolvedDeadlineFilter,
        showAll,
        offset: safeOffset,
        limit: safeLimit,
      });
    }
  },
  {
    condition: (
      {
        showAll = false,
        deadlineFilter,
        offset = 0,
        limit = NOTIFICATION_CENTER_PAGE_SIZE,
      } = {},
      { getState }
    ) => {
      const resolvedDeadlineFilter = resolveDeadlinesFilter(
        deadlineFilter,
        getState().notificationCenter?.deadlinesScopeFilter
      );
      const key = deadlinesRequestKey(
        showAll,
        resolvedDeadlineFilter,
        Math.max(0, Number(offset) || 0),
        Math.max(1, Number(limit) || NOTIFICATION_CENTER_PAGE_SIZE)
      );
      if (inFlightDeadlinesRequests.has(key)) {
        return false;
      }
      inFlightDeadlinesRequests.add(key);
      return true;
    },
  }
);

export const fetchNotificationCenterTable = createAsyncThunk(
  "notificationCenter/fetchTable",
  async (
    {
      tableId,
      offset = 0,
      limit = NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
      showAll = false,
      declarationType,
    } = {},
    { rejectWithValue, getState }
  ) => {
    const resolvedDeclarationType =
      tableId === NOTIFICATION_CENTER_REPORTING_TAB_ID
        ? resolveDeclarationTypeFilter(
            declarationType,
            getState().notificationCenter?.declarationTypeFilter
          )
        : NOTIFICATION_CENTER_DECLARATION_TYPE.ALL;
    const registrationsFilters = resolveRegistrationsFilters(
      tableId,
      getState().notificationCenter
    );
    const registrationsFilterSignature =
      getNotificationCenterRegistrationsFilterSignature(registrationsFilters);

    try {
      if (!tableId) {
        throw new Error("tableId is required");
      }
      if (!isNotificationCenterPaginatedTable(tableId)) {
        throw new Error(`tableId "${tableId}" does not support pagination`);
      }

      const response = await getNotificationCenterTable(tableId, {
        offset,
        limit,
        ...getNotificationCenterShowAllQueryParams({ showAll, tableId }),
        ...getNotificationCenterDeclarationTypeQueryParams(
          tableId === NOTIFICATION_CENTER_REPORTING_TAB_ID
            ? resolvedDeclarationType
            : NOTIFICATION_CENTER_DECLARATION_TYPE.ALL
        ),
        ...getNotificationCenterRegistrationsQueryParams(registrationsFilters),
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }

      const data = await response.json();
      return {
        ...normalizeNotificationCenterTableResponse(data, tableId, offset, limit),
        silent,
        declarationType: resolvedDeclarationType,
        showAll,
        registrationsFilterSignature,
      };
    } catch (e) {
      return rejectWithValue({
        tableId,
        message: e?.message || "Error",
        declarationType: resolvedDeclarationType,
        showAll,
        offset,
        limit,
        registrationsFilterSignature,
      });
    }
  },
  {
    condition: (
      {
        tableId,
        offset = 0,
        limit = NOTIFICATION_CENTER_PAGE_SIZE,
        showAll = false,
        declarationType,
      } = {},
      { getState }
    ) => {
      if (!tableId || !isNotificationCenterPaginatedTable(tableId)) {
        return false;
      }
      const resolvedDeclarationType =
        tableId === NOTIFICATION_CENTER_REPORTING_TAB_ID
          ? resolveDeclarationTypeFilter(
              declarationType,
              getState().notificationCenter?.declarationTypeFilter
            )
          : NOTIFICATION_CENTER_DECLARATION_TYPE.ALL;
      const key = tableRequestKey(
        tableId,
        offset,
        limit,
        showAll,
        resolvedDeclarationType,
        getNotificationCenterRegistrationsFilterSignature(
          resolveRegistrationsFilters(tableId, getState().notificationCenter)
        )
      );
      if (inFlightTableRequests.has(key)) {
        return false;
      }
      inFlightTableRequests.add(key);
      return true;
    },
  }
);

export const fetchNotificationCenterCounters = createAsyncThunk(
  "notificationCenter/fetchCounters",
  async ({ silent = false, showAll = false } = {}, { rejectWithValue }) => {
    try {
      const response = await getNotificationCenterCounters(
        getNotificationCenterShowAllQueryParams({ showAll })
      );
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return normalizeNotificationCenterCounters(data);
    } catch (e) {
      return rejectWithValue(
        e?.message || "Error"
      );
    }
  }
);

/** Reload active table page and bell/summary counters after read, close, etc. */
export const refreshNotificationCenterAfterAction = createAsyncThunk(
  "notificationCenter/refreshAfterAction",
  async (arg = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState().notificationCenter;
      const tableId = arg.tableId ?? state.lastFetchedTableId;
      const tabSection = tableId ? state.dataByTab[tableId]?.default : null;
      const user = getState().user?.user;
      const showAll = resolveNotificationCenterShowAllForApi({
        visibilityFilter: state.visibilityFilter,
        tableId,
        user,
      });

      const tasks = [
        dispatch(fetchNotificationCenterCounters({ silent: true, showAll })).unwrap(),
      ];

      if (tableId === NOTIFICATION_CENTER_DEADLINES_TAB_ID) {
        tasks.push(
          dispatch(
            fetchNotificationCenterDeadlines({
              silent: true,
              showAll,
              offset: arg.offset ?? tabSection?.offset ?? 0,
              limit: arg.limit ?? tabSection?.limit ?? NOTIFICATION_CENTER_PAGE_SIZE,
            })
          ).unwrap()
        );
      } else if (tableId) {
        tasks.push(
          dispatch(
            fetchNotificationCenterTable({
              tableId,
              offset: arg.offset ?? tabSection?.offset ?? 0,
              limit: arg.limit ?? tabSection?.limit ?? NOTIFICATION_CENTER_PAGE_SIZE,
              silent: true,
              showAll,
              declarationType:
                tableId === NOTIFICATION_CENTER_REPORTING_TAB_ID
                  ? state.declarationTypeFilter
                  : undefined,
            })
          ).unwrap()
        );
      }

      await Promise.all(tasks);
    } catch (e) {
      return rejectWithValue(
        e?.message || "Error"
      );
    }
  }
);

function getNotificationCenterInitialState() {
  const storedFilters = loadNotificationCenterFiltersFromStorage();

  return {
    dataByTab: createEmptyByTab(),
    lastFetchedTableId: null,
    counters: normalizeNotificationCenterCounters(null),
    countersLoading: false,
    countersError: null,
    countersHydrated: false,
    visibilityFilter: storedFilters.visibilityFilter,
    deadlinesScopeFilter: storedFilters.deadlinesScopeFilter,
    declarationTypeFilter: storedFilters.declarationTypeFilter,
    registrationsCompanyFilter: null,
    registrationsOrderDateFilter: null,
    registrationsJurisdictionFilter: [],
    registrationsTypeOfRegistrationFilter: null,
    registrationsSubcategoryFilter: [],
    registrationsStatusFilter: [],
    registrationsDateLastStatusChangedFilter: null,
    registrationsRegistratorFilter: [],
    registrationsEstimatedRegistrationDateFilter: null,
    registrationsFilterOptions: createEmptyRegistrationsFilterOptions(),
    registrationsHighlightError: null,
  };
}

const initialState = getNotificationCenterInitialState();

const notificationCenterSlice = createSlice({
  name: "notificationCenter",
  initialState,
  reducers: {
    rehydrateNotificationCenterFiltersFromStorage: (state) => {
      applyStoredNotificationCenterFiltersToState(state);
    },
    setNotificationCenterFilter: (state, action) => {
      const { reduxKey, value } = action.payload ?? {};
      if (!reduxKey) {
        return;
      }

      if (reduxKey === "visibilityFilter") {
        state.visibilityFilter =
          value === NOTIFICATION_CENTER_VISIBILITY.ALL
            ? NOTIFICATION_CENTER_VISIBILITY.ALL
            : NOTIFICATION_CENTER_VISIBILITY.MINE;
      } else if (reduxKey === "deadlinesScopeFilter") {
        const allowed = Object.values(NOTIFICATION_CENTER_DEADLINES_SCOPE);
        state.deadlinesScopeFilter = allowed.includes(value)
          ? value
          : NOTIFICATION_CENTER_DEADLINES_SCOPE.NEXT_3_DAYS;
      } else if (reduxKey === "declarationTypeFilter") {
        const allowed = Object.values(NOTIFICATION_CENTER_DECLARATION_TYPE);
        state.declarationTypeFilter = allowed.includes(value)
          ? value
          : NOTIFICATION_CENTER_DECLARATION_TYPE.ALL;
      } else if (reduxKey === "registrationsCompanyFilter") {
        state.registrationsCompanyFilter =
          normalizeRegistrationsCompanyFilter(value);
      } else if (reduxKey === "registrationsOrderDateFilter") {
        state.registrationsOrderDateFilter =
          normalizeRegistrationsOrderDateFilter(value);
      } else if (reduxKey === "registrationsJurisdictionFilter") {
        state.registrationsJurisdictionFilter =
          normalizeRegistrationsJurisdictionFilter(value);
      } else if (reduxKey === "registrationsTypeOfRegistrationFilter") {
        state.registrationsTypeOfRegistrationFilter =
          normalizeRegistrationsTypeFilter(value);
        if (
          String(state.registrationsTypeOfRegistrationFilter).toUpperCase() !==
          "EPR"
        ) {
          state.registrationsSubcategoryFilter = [];
        }
      } else if (reduxKey === "registrationsSubcategoryFilter") {
        state.registrationsSubcategoryFilter =
          normalizeRegistrationsMultiSelectFilter(value);
      } else if (reduxKey === "registrationsStatusFilter") {
        state.registrationsStatusFilter =
          normalizeRegistrationsMultiSelectFilter(value);
      } else if (
        reduxKey === "registrationsDateLastStatusChangedFilter"
      ) {
        state.registrationsDateLastStatusChangedFilter =
          normalizeRegistrationsDateFilter(value);
      } else if (reduxKey === "registrationsRegistratorFilter") {
        state.registrationsRegistratorFilter =
          normalizeRegistrationsMultiSelectFilter(value);
      } else if (
        reduxKey === "registrationsEstimatedRegistrationDateFilter"
      ) {
        state.registrationsEstimatedRegistrationDateFilter =
          normalizeRegistrationsDateFilter(value);
      }

      saveNotificationCenterFiltersToStorage(state);
    },
    clearNotificationCenterError: (state, action) => {
      const tableId = action.payload?.tableId ?? state.lastFetchedTableId;
      if (tableId && state.dataByTab[tableId]) {
        state.dataByTab[tableId].error = null;
        return;
      }
      for (const tabId of Object.keys(state.dataByTab)) {
        if (state.dataByTab[tabId]) {
          state.dataByTab[tabId].error = null;
        }
      }
    },
    clearNotificationCenterHighlightError: (state) => {
      state.registrationsHighlightError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationCenterTable.pending, (state, action) => {
        const { tableId, limit } = action.meta.arg ?? {};
        if (!tableId) return;

        if (!state.dataByTab[tableId]) {
          state.dataByTab[tableId] = createEmptyTabTableState(limit);
        }

        if (!isSilentFetch(action.meta?.arg)) {
          state.dataByTab[tableId].loading = true;
        }
        state.dataByTab[tableId].error = null;
        state.lastFetchedTableId = tableId;
      })
      .addCase(fetchNotificationCenterTable.fulfilled, (state, action) => {
        const { tableId, items, total, offset, limit } = action.payload;
        const showAll = Boolean(action.payload?.showAll ?? action.meta?.arg?.showAll);
        const declarationType =
          action.payload?.declarationType ?? action.meta?.arg?.declarationType;
        inFlightTableRequests.delete(
          tableRequestKey(
            tableId,
            offset,
            limit,
            showAll,
            declarationType,
            action.payload?.registrationsFilterSignature
          )
        );
        if (tableId === NOTIFICATION_CENTER_REGISTRATIONS_TAB_ID) {
          state.registrationsFilterOptions =
            collectNotificationCenterRegistrationsFilterOptions(
              items,
              state.registrationsFilterOptions
            );
        }
        state.dataByTab[tableId] = {
          loading: false,
          error: null,
          default: {
            items,
            total,
            offset,
            limit,
          },
        };
        state.lastFetchedTableId = tableId;
      })
      .addCase(fetchNotificationCenterDeadlines.pending, (state, action) => {
        const tableId = NOTIFICATION_CENTER_DEADLINES_TAB_ID;
        const { limit } = action.meta.arg ?? {};
        if (!state.dataByTab[tableId]) {
          state.dataByTab[tableId] = createEmptyTabTableState(limit);
        }
        if (!isSilentFetch(action.meta?.arg)) {
          state.dataByTab[tableId].loading = true;
        }
        state.dataByTab[tableId].error = null;
        state.lastFetchedTableId = tableId;
      })
      .addCase(fetchNotificationCenterDeadlines.fulfilled, (state, action) => {
        const {
          offset = 0,
          limit = NOTIFICATION_CENTER_PAGE_SIZE,
          showAll = false,
          deadlineFilter,
        } = action.meta?.arg ?? {};
        inFlightDeadlinesRequests.delete(
          deadlinesRequestKey(
            Boolean(action.payload?.showAll ?? showAll),
            action.payload?.deadlineFilter ?? deadlineFilter,
            offset,
            limit
          )
        );
        const {
          tableId,
          items,
          total,
          offset: responseOffset,
          limit: responseLimit,
        } = action.payload;

        state.dataByTab[tableId] = {
          loading: false,
          error: null,
          default: {
            items,
            total,
            offset: responseOffset,
            limit: responseLimit,
          },
        };
        state.lastFetchedTableId = tableId;
      })
      .addCase(fetchNotificationCenterDeadlines.rejected, (state, action) => {
        const {
          offset = 0,
          limit = NOTIFICATION_CENTER_PAGE_SIZE,
          showAll = false,
          deadlineFilter,
        } = action.meta?.arg ?? {};
        inFlightDeadlinesRequests.delete(
          deadlinesRequestKey(
            Boolean(action.payload?.showAll ?? showAll),
            action.payload?.deadlineFilter ?? deadlineFilter,
            action.payload?.offset ?? offset,
            action.payload?.limit ?? limit
          )
        );
        const tableId =
          action.payload?.tableId ?? NOTIFICATION_CENTER_DEADLINES_TAB_ID;
        const message =
          action.payload?.message ||
          action.error?.message ||
          "Error";

        if (!state.dataByTab[tableId]) {
          state.dataByTab[tableId] = createEmptyTabTableState();
        }
        state.dataByTab[tableId].loading = false;
        state.dataByTab[tableId].error = message;
      })
      .addCase(fetchNotificationCenterTable.rejected, (state, action) => {
        const tableId = action.payload?.tableId ?? action.meta?.arg?.tableId;
        const {
          offset = 0,
          limit = NOTIFICATION_CENTER_PAGE_SIZE,
          showAll = false,
          declarationType,
        } = action.meta?.arg ?? {};
        if (tableId) {
          inFlightTableRequests.delete(
            tableRequestKey(
              tableId,
              action.payload?.offset ?? offset,
              action.payload?.limit ?? limit,
              Boolean(action.payload?.showAll ?? showAll),
              action.payload?.declarationType ?? declarationType,
              action.payload?.registrationsFilterSignature
            )
          );
        }
        const message =
          action.payload?.message ||
          action.error?.message ||
          "Error";

        if (tableId) {
          if (!state.dataByTab[tableId]) {
            state.dataByTab[tableId] = createEmptyTabTableState();
          }
          state.dataByTab[tableId].loading = false;
          state.dataByTab[tableId].error = message;
        }
      })
      .addCase(fetchNotificationCenterCounters.pending, (state, action) => {
        if (!isSilentFetch(action.meta?.arg)) {
          state.countersLoading = true;
        }
        state.countersError = null;
      })
      .addCase(fetchNotificationCenterCounters.fulfilled, (state, action) => {
        state.countersLoading = false;
        state.counters = action.payload ?? normalizeNotificationCenterCounters(null);
        state.countersHydrated = true;
      })
      .addCase(fetchNotificationCenterCounters.rejected, (state, action) => {
        state.countersLoading = false;
        state.countersError =
          action.payload || action.error?.message || "Error";
      })
      .addCase(updateRegistrationTrackerHighlighted.pending, (state) => {
        state.registrationsHighlightError = null;
      })
      // The flag lives on the tracker record, so keep the Registrations rows in
      // sync with the toggle instead of refetching the whole table.
      .addCase(updateRegistrationTrackerHighlighted.fulfilled, (state, action) => {
        const { trackerId, highlighted } = action.payload ?? {};
        const section =
          state.dataByTab[NOTIFICATION_CENTER_REGISTRATIONS_TAB_ID]?.default;
        if (!trackerId || !Array.isArray(section?.items)) {
          return;
        }
        section.items = section.items.map((item) =>
          getNotificationCenterRegistrationTrackerId(item) === String(trackerId)
            ? { ...item, highlighted: highlighted === true }
            : item
        );
      })
      .addCase(updateRegistrationTrackerHighlighted.rejected, (state, action) => {
        state.registrationsHighlightError =
          action.payload ||
          action.error?.message ||
          "Failed to update the attention flag";
      })
      .addCase(login.fulfilled, (state) => {
        applyStoredNotificationCenterFiltersToState(state);
      });
  },
});

export const {
  clearNotificationCenterError,
  clearNotificationCenterHighlightError,
  setNotificationCenterFilter,
  rehydrateNotificationCenterFiltersFromStorage,
} = notificationCenterSlice.actions;

const notificationCenterReducer = (state, action) => {
  if (state === undefined) {
    return notificationCenterSlice.reducer(getNotificationCenterInitialState(), action);
  }
  return notificationCenterSlice.reducer(state, action);
};

export default notificationCenterReducer;
